import { useState, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { useAppDispatcher } from 'src/redux/store';
import { markLoanBadDebt, reverseLoanBadDebt } from 'src/redux/slices/loan-agreement';

type Props = {
  agreementId: string;
  // 'mark' flags the loan as bad debt; 'reverse' undoes it.
  mode: 'mark' | 'reverse';
  open: boolean;
  onClose: VoidFunction;
};

export default function LoanBadDebtDialog({ agreementId, mode, open, onClose }: Props) {
  const dispatch = useAppDispatcher();

  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const isMark = mode === 'mark';

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const thunk = isMark ? markLoanBadDebt : reverseLoanBadDebt;
      await dispatch(thunk({ id: agreementId, reason: reason.trim() })).unwrap();
      enqueueSnackbar(isMark ? 'Loan marked as bad debt' : 'Bad debt mark reversed');
      onClose();
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>{isMark ? 'Mark as Bad Debt' : 'Reverse Bad Debt'}</DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {isMark
            ? 'The agreement moves to the Bad Debt tab, and regular users will be blocked from creating a new loan for this person. Everyone on the loan receipts list is emailed.'
            : 'The agreement moves back to the Active tab and the borrower is no longer blocked.'}
        </Typography>

        <TextField
          autoFocus
          fullWidth
          multiline
          rows={3}
          required
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          inputProps={{ maxLength: 1000 }}
        />
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color={isMark ? 'error' : 'primary'}
          loading={submitting}
          disabled={!reason.trim()}
          onClick={handleSubmit}
        >
          {isMark ? 'Mark as Bad Debt' : 'Reverse'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
